import { useContext, useEffect, useState } from 'react';
import { SidebarContext } from '../../../context/SidebarContext.js';
import SectionMenuLeft from '../../../components/SectionMenuLeft/SectionMenuLeft.jsx';
import './UserSettings.css';
import { config } from '../../../config.js';
import { ToastContainer, toast } from 'react-toastify';
import { RiDeleteBin6Line } from "react-icons/ri";

function UserSettings() {
	const { isSidebar } = useContext(SidebarContext);

	const [userSettings, setUserSettings] = useState(null);
	const [formValues, setFormValues] = useState({});
	const [extraFields, setExtraFields] = useState([]);
	const [removedFields, setRemovedFields] = useState([]);
	const [isLoading, setIsLoading] = useState(false);

	// =========================
	// FETCH SETTINGS
	// =========================
	const fetchSettings = async () => {
		try {
			const response = await fetch(`${config?.backEndBaseUrl}constants/global?type=user`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'x-dashboard-request': 'true',
				},
			});

			if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

			const result = await response.json();

			if (result.status) {
				setUserSettings(result.data);
				setFormValues(result.data);
			} else {
				setUserSettings(null);
				setFormValues({});
			}
		} catch (error) {
			console.error('Fetching settings failed:', error);
			setUserSettings(null);
			setFormValues({});
		}
	};

	useEffect(() => {
		fetchSettings();
	}, []);

	// =========================
	// HANDLE PRIMITIVE CHANGE
	// =========================
	const handlePrimitiveChange = (key, value) => {
		setFormValues(prev => ({
			...prev,
			[key]: Number(value)
		}));
	};

	// =========================
	// HANDLE NESTED CHANGE
	// =========================
	const handleNestedChange = (parentKey, childKey, value) => {
		setFormValues(prev => ({
			...prev,
			[parentKey]: {
				...prev[parentKey],
				[childKey]: Number(value)
			}
		}));
	};

	// =========================
	// EXTRA FIELD LOGIC
	// =========================
	const addExtraField = () => {
		setExtraFields(prev => [...prev, { key: '', value: '' }]);
	};

	const removeExtraField = (index) => {
		setExtraFields(prev => prev.filter((_, i) => i !== index));
	};

	const removeOriginalField = (key) => {
		setRemovedFields(prev => [...prev, key]);
		setFormValues(prev => {
			const copy = { ...prev };
			delete copy[key];
			return copy;
		});
	};

	const handleExtraChange = (index, field, value) => {
		setExtraFields(prev =>
			prev.map((f, i) =>
				i === index ? { ...f, [field]: value } : f
			)
		);
	};

	// =========================
	// SUBMIT
	// =========================
	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsLoading(true);

		const dataToSubmit = { ...formValues };

		// Add extra fields
		extraFields.forEach(f => {
			if (f.key && f.value) {
				dataToSubmit[f.key] = isNaN(f.value) ? f.value : Number(f.value);
			}
		});

		// Remove deleted fields
		removedFields.forEach(key => delete dataToSubmit[key]);

		try {
			const response = await fetch(`${config?.backEndBaseUrl}constants/global?type=user`, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
					'x-dashboard-request': 'true',
				},
				body: JSON.stringify(dataToSubmit),
			});

			const result = await response.json();

			if (result.status) {
				toast.success('User settings updated successfully');
				setUserSettings(result.data);
				setFormValues(result.data);
				setExtraFields([]);
				setRemovedFields([]);
			} else {
				toast.error(result.message || 'Failed to update settings');
			}
		} catch (error) {
			console.error('Update failed:', error);
			toast.error('Update failed');
		} finally {
			setIsLoading(false);
		}
	};

	// =========================
	// RENDER
	// =========================
	return (
		<div className="body">
			<div id="wrapper">
				<div id="page">
					<div className="layout-wrap">
						<SectionMenuLeft />
						<div className="section-content-right">
							<div
								className="main-content"
								style={{ paddingLeft: isSidebar ? '280px' : '0px' }}
							>
								<div className="main-content-inner">
									<div className="main-content-wrap">
										<ToastContainer />
										<form onSubmit={handleSubmit}>
											<div className="card shadow-sm border-0">
												<div className="card-header bg-white d-flex justify-content-between align-items-center">
													<h5 className="mb-0 fw-semibold">User Global Settings</h5>
												</div>

												<div className="card-body">

													{/* ORIGINAL SETTINGS */}
													{userSettings &&
														Object.keys(userSettings).map((key) => {
															if (key === 'id' || removedFields.includes(key)) return null;

															const value = formValues[key];

															// ===== Nested Object (dustingRate etc) =====
															if (typeof value === 'object' && value !== null) {
																return (
																	<div key={key} className="mb-4 p-3 border rounded bg-light">
																		<h6 className="fw-semibold mb-3 text-primary text-capitalize">
																			{key}
																		</h6>

																		<div className="row g-3">
																			{Object.keys(value).map((nestedKey) => (
																				<div className="col-md-3" key={nestedKey}>
																					<label className="form-label text-muted text-capitalize">
																						{nestedKey}
																					</label>
																					<input
																						type="number"
																						className="form-control"
																						value={value[nestedKey]}
																						onChange={(e) =>
																							handleNestedChange(
																								key,
																								nestedKey,
																								e.target.value
																							)
																						}
																					/>
																				</div>
																			))}
																		</div>
																	</div>
																);
															}

															// ===== Primitive Fields =====
															return (
																<div className="row mb-3 align-items-center" key={key}>
																	<div className="col-md-4">
																		<label className="form-label text-muted text-capitalize">
																			{key}
																		</label>
																	</div>

																	<div className="col-md-6">
																		<input
																			type="number"
																			className="form-control"
																			value={value}
																			onChange={(e) =>
																				handlePrimitiveChange(key, e.target.value)
																			}
																		/>
																	</div>

																	<div className="col-md-2 text-end">
																		<button
																			type="button"
																			className="btn btn-sm btn-outline-danger"
																			onClick={() => removeOriginalField(key)}
																		>
																			<RiDeleteBin6Line />
																		</button>
																	</div>
																</div>
															);
														})}

													{/* EXTRA FIELDS */}
													{extraFields.map((f, index) => (
														<div className="row mb-3 align-items-center" key={index}>
															<div className="col-md-5">
																<input
																	type="text"
																	className="form-control"
																	placeholder="Field Name"
																	value={f.key}
																	onChange={(e) =>
																		handleExtraChange(index, 'key', e.target.value)
																	}
																/>
															</div>

															<div className="col-md-5">
																<input
																	type="text"
																	className="form-control"
																	placeholder="Value"
																	value={f.value}
																	onChange={(e) =>
																		handleExtraChange(index, 'value', e.target.value)
																	}
																/>
															</div>

															<div className="col-md-2 text-end">
																<button
																	type="button"
																	className="btn btn-sm btn-outline-danger"
																	onClick={() => removeExtraField(index)}
																>
																	<RiDeleteBin6Line />
																</button>
															</div>
														</div>
													))}

												</div>

												<div className="card-footer bg-white d-flex justify-content-between">
													<button
														type="button"
														className="btn btn-outline-primary"
														onClick={addExtraField}>
														+ Add Field
													</button>

													<button
														type="submit"
														className="btn btn-success"
														disabled={isLoading}>
														{isLoading ? "Saving..." : "Save Changes"}
													</button>
												</div>
											</div>
										</form>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default UserSettings;